/**
 * WeeklyScheduleTab — Planning hebdomadaire du personnel.
 *
 * Grille : Sections (Cuisine, Salle, Entretien, Salle de jeux) x Jours (Lun-Dim)
 * Chaque cellule contient 2 sous-cellules :
 *   - Équipe 1 (matin) : 9h-17h
 *   - Équipe 2 (soir)  : 17h-23h
 *
 * Actions :
 *   - Cliquer sur "+" dans une sous-cellule ouvre un picker d'employé
 *   - "×" sur un badge employé le retire de l'assignation
 *   - Navigation semaine par semaine
 *   - Bouton "Copier la semaine précédente"
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Sun, Moon, X, Plus, Copy, Loader2, RefreshCw,
  ChefHat, Utensils, Sparkles, Gamepad2, Printer, Trash2, CalendarDays, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { key: "mon", label: "Lundi",    short: "Lun" },
  { key: "tue", label: "Mardi",    short: "Mar" },
  { key: "wed", label: "Mercredi", short: "Mer" },
  { key: "thu", label: "Jeudi",    short: "Jeu" },
  { key: "fri", label: "Vendredi", short: "Ven" },
  { key: "sat", label: "Samedi",   short: "Sam" },
  { key: "sun", label: "Dimanche", short: "Dim" },
];

const SECTIONS = [
  // Palette pro imprimable — Bleu marine dominant avec nuances subtiles par section
  { key: "cuisine",   label: "Cuisine",         icon: ChefHat,   accent: "#2C4A6E", accentLight: "#eaf0f7" }, // Marine moyen (chaud)
  { key: "salle",     label: "Salle",           icon: Utensils,  accent: "#14274E", accentLight: "#e2e8f0" }, // Marine profond
  { key: "entretien", label: "Agent entretien", icon: Sparkles,  accent: "#2C4F5A", accentLight: "#e6eef0" }, // Marine bleu-vert
  { key: "jeux",      label: "Salle de jeux",   icon: Gamepad2,  accent: "#3B3A6E", accentLight: "#ebeaf3" }, // Marine indigo
];

const TEAMS = [
  { key: "morning", label: "Équipe 1", hours: "9h – 17h",  icon: Sun,  accent: "#D4A76A" }, // Or ocre (contraste chaud sur marine)
  { key: "evening", label: "Équipe 2", hours: "17h – 23h", icon: Moon, accent: "#94A3B8" }, // Gris perle (contraste froid)
];

// Helpers dates
const mondayOf = (d = new Date()) => {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Monday=0
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
};
const toISO = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtDayShort = (d) => d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
const fmtWeekRange = (mon) => {
  const sun = addDays(mon, 6);
  const m = mon.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  const s = sun.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  return `${m} → ${s}`;
};

const WeeklyScheduleTab = ({ currentUser }) => {
  const [weekMonday, setWeekMonday] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerCell, setPickerCell] = useState(null); // { day, team, section }
  const [pickerSearch, setPickerSearch] = useState("");

  const weekStart = toISO(weekMonday);

  // Rôle : Admin peut valider et supprimer les entrées validées.
  //        Manager peut composer (crée en "pending" en attente de validation Admin).
  const isAdmin = currentUser?.role === "admin";
  const canEdit = isAdmin || currentUser?.role === "manager";
  const authHeaders = useMemo(() => ({
    "x-user-role": currentUser?.role || "manager",
    "x-user-name": currentUser?.full_name || currentUser?.username || currentUser?.name || "Utilisateur",
  }), [currentUser]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/schedules`, { params: { week_start: weekStart } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement du planning");
    } finally { setLoading(false); }
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  // Index rapide : entries par (day, team, section)
  const cellIndex = useMemo(() => {
    const map = new Map();
    for (const e of data?.entries || []) {
      const key = `${e.day}|${e.team}|${e.section}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return map;
  }, [data]);

  const goPrevWeek = () => setWeekMonday((d) => addDays(d, -7));
  const goNextWeek = () => setWeekMonday((d) => addDays(d, 7));
  const goToday = () => setWeekMonday(mondayOf(new Date()));

  const assign = async (salaryId) => {
    if (!pickerCell) return;
    setBusy(true);
    try {
      await axios.post(`${API}/schedules`, {
        week_start: weekStart,
        day: pickerCell.day,
        team: pickerCell.team,
        section: pickerCell.section,
        salary_id: salaryId,
      }, { headers: authHeaders });
      toast.success(isAdmin ? "Employé assigné" : "Ajouté — en attente de validation Admin");
      setPickerCell(null);
      setPickerSearch("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const removeEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/schedules/${entryId}`, { headers: authHeaders });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const validateEntry = async (entryId) => {
    try {
      await axios.post(`${API}/schedules/${entryId}/validate`, null, { headers: authHeaders });
      toast.success("Entrée validée ✓");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const validateWeek = async () => {
    if (!window.confirm(`Valider toutes les entrées en attente pour la semaine du ${fmtWeekRange(weekMonday)} ?`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/schedules/validate-week`, null, {
        params: { week_start: weekStart },
        headers: authHeaders,
      });
      toast.success(`${res.data?.validated || 0} entrée(s) validée(s) ✓`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const copyPreviousWeek = async () => {
    if (!window.confirm("Copier le planning de la semaine précédente vers cette semaine ?\n\nLes entrées déjà présentes ne seront pas écrasées.")) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/schedules/copy-previous`, null, { params: { week_start: weekStart }, headers: authHeaders });
      toast.success(`${res.data?.copied || 0} entrée(s) copiée(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const clearWeek = async () => {
    if (!window.confirm(`Effacer TOUT le planning de la semaine du ${fmtWeekRange(weekMonday)} ?\n\nCette action est irréversible.`)) return;
    setBusy(true);
    try {
      const res = await axios.delete(`${API}/schedules/week/${weekStart}`, { headers: authHeaders });
      toast.success(`${res.data?.deleted || 0} entrée(s) supprimée(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const printSchedule = () => {
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) { toast.error("Popup bloqué"); return; }
    const rows = SECTIONS.map((s) => {
      const cells = DAYS.map((_, dayIdx) => {
        return TEAMS.map((t) => {
          const entries = cellIndex.get(`${dayIdx}|${t.key}|${s.key}`) || [];
          const names = entries.map(e => `<div style="padding:1.5px 0;font-size:10px;color:#0f172a;font-weight:600">${e.salary_name}</div>`).join("");
          return `<div style="min-height:32px;padding:4px 5px;border-top:1px solid #cbd5e1"><b style="color:${t.accent};font-size:8.5px;letter-spacing:0.06em;text-transform:uppercase;font-weight:800">${t.label} · ${t.hours}</b>${names || '<div style="color:#94a3b8;font-size:9.5px;font-style:italic;padding-top:1px">—</div>'}</div>`;
        }).join("");
      }).map((cellHtml) => `<td style="border:1px solid #64748b;vertical-align:top;padding:0;background:#fff">${cellHtml}</td>`).join("");
      return `<tr><td style="border:1.5px solid #334155;padding:8px 10px;font-weight:900;background:${s.accent};color:white;text-transform:uppercase;letter-spacing:0.08em;font-size:10px;line-height:1.3">${s.label}</td>${cells}</tr>`;
    }).join("");
    const daysHeader = DAYS.map((d, i) => {
      const dayD = addDays(weekMonday, i);
      return `<th style="background:#0F1F3D;color:white;border:1.5px solid #334155;padding:7px 4px;font-size:10.5px;text-transform:uppercase;letter-spacing:0.1em;font-weight:800">${d.short}<br/><span style="font-weight:500;font-size:9.5px;color:#cbd5e1;letter-spacing:0.02em">${fmtDayShort(dayD)}</span></th>`;
    }).join("");
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Planning hebdo · ${fmtWeekRange(weekMonday)}</title>
<style>
@media print { @page { size: A4 landscape; margin: 10mm 8mm; } }
* { box-sizing: border-box; }
body { font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif; padding: 12px 10px; margin: 0; color: #0f172a; -webkit-print-color-adjust: exact; print-color-adjust: exact; -webkit-font-smoothing: antialiased; }
.head { display: flex; align-items: center; justify-content: space-between; padding-bottom: 10px; margin-bottom: 12px; border-bottom: 2.5px solid #14274E; }
.brand h1 { color: #0F1F3D; font-size: 20px; letter-spacing: 0.02em; margin: 0; font-weight: 900; }
.brand .sub { color: #334155; font-size: 11px; margin-top: 3px; font-weight: 500; }
.doc-title { text-align: right; }
.doc-title .kicker { font-size: 9.5px; letter-spacing: 0.15em; text-transform: uppercase; color: #14274E; font-weight: 800; }
.doc-title .title { font-size: 16px; font-weight: 900; letter-spacing: 0.02em; color: #0F1F3D; margin-top: 2px; }
.legend { display: flex; gap: 12px; margin-bottom: 10px; font-size: 10px; color: #0f172a; font-weight: 600; }
.legend-item { display: inline-flex; align-items: center; gap: 5px; padding: 3px 8px; border: 1px solid #cbd5e1; border-radius: 3px; background: #f8fafc; }
.legend-item .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
table { border-collapse: collapse; width: 100%; border: 1.5px solid #334155; }
.footer { margin-top: 12px; padding-top: 6px; border-top: 1px dotted #94a3b8; display: flex; justify-content: space-between; font-size: 9px; color: #334155; font-weight: 500; }
</style></head><body>

<div class="head">
  <div class="brand">
    <h1>ESPACE MAXO SARL</h1>
    <div class="sub">Restaurant · Jeux VR · Livraison</div>
  </div>
  <div class="doc-title">
    <div class="kicker">Planning hebdomadaire du personnel</div>
    <div class="title">Semaine du ${fmtWeekRange(weekMonday)}</div>
    <div style="font-size:9.5px;color:#334155;margin-top:3px;font-weight:500">Généré le ${new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>
  </div>
</div>

<div class="legend">
  ${TEAMS.map(t => `<div class="legend-item"><span class="dot" style="background:${t.accent}"></span> <b>${t.label}</b> <span style="color:#334155">${t.hours}</span></div>`).join("")}
</div>

<table>
  <thead><tr><th style="background:#0F1F3D;color:white;border:1.5px solid #334155;padding:7px 8px;font-size:10.5px;text-transform:uppercase;letter-spacing:0.1em;width:130px;font-weight:800">Section</th>${daysHeader}</tr></thead>
  <tbody>${rows}</tbody>
</table>

<div class="footer">
  <div>ESPACE MAXO SARL — Document interne · Confidentiel</div>
  <div>Planning hebdo · ${fmtWeekRange(weekMonday)}</div>
</div>

<script>window.addEventListener('load', () => setTimeout(() => window.print(), 300));</script>
</body></html>`);
    w.document.close();
  };

  // Employés filtrés dans le picker
  const filteredEmployees = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const list = data?.employees || [];
    if (!q) return list;
    return list.filter((e) =>
      (e.name || "").toLowerCase().includes(q) ||
      (e.role_title || "").toLowerCase().includes(q) ||
      (e.matricule || "").toLowerCase().includes(q)
    );
  }, [data, pickerSearch]);

  // Employé déjà assigné dans la cellule courante ? Empêche double assignation
  const currentCellSalaryIds = useMemo(() => {
    if (!pickerCell) return new Set();
    const key = `${pickerCell.day}|${pickerCell.team}|${pickerCell.section}`;
    return new Set((cellIndex.get(key) || []).map(e => e.salary_id));
  }, [pickerCell, cellIndex]);

  // Nombre d'entrées en attente de validation Admin (pour le bandeau + badge)
  const pendingCount = useMemo(() => (data?.entries || []).filter(e => e.status === "pending").length, [data]);

  return (
    <div className="space-y-3" data-testid="weekly-schedule-tab">
      {/* Barre navigation semaine */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
            <CalendarDays className="w-5 h-5 text-indigo-300" strokeWidth={2.3} />
          </div>
          <div className="min-w-0">
            <p className="text-white font-bold text-[15px] leading-tight">Planning hebdomadaire</p>
            <p className="text-slate-500 text-[11px] leading-tight">
              Semaine du <b className="text-slate-300">{fmtWeekRange(weekMonday)}</b>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-lg px-1 py-0.5 flex-shrink-0">
          <Button size="sm" variant="ghost" onClick={goPrevWeek} className="h-7 w-7 p-0 text-slate-300" data-testid="sched-prev-week">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <button onClick={goToday} className="text-[11px] font-bold text-white px-2 h-7 hover:text-amber-300" data-testid="sched-current-week">
            Aujourd&apos;hui
          </button>
          <Button size="sm" variant="ghost" onClick={goNextWeek} className="h-7 w-7 p-0 text-slate-300" data-testid="sched-next-week">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {canEdit && (
          <>
            <Button
              size="sm" variant="outline"
              onClick={copyPreviousWeek} disabled={busy || loading}
              className="border-blue-600/50 text-blue-300 hover:bg-blue-500/10 h-9 text-[11px]"
              data-testid="sched-copy-previous"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> Copier semaine précédente
            </Button>
            {isAdmin && (
              <>
                {pendingCount > 0 && (
                  <Button
                    size="sm"
                    onClick={validateWeek} disabled={busy || loading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-[11px]"
                    data-testid="sched-validate-week"
                    title={`${pendingCount} entrée(s) en attente de validation`}
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Valider la semaine ({pendingCount})
                  </Button>
                )}
                <Button
                  size="sm" variant="outline"
                  onClick={clearWeek} disabled={busy || loading || !data?.count}
                  className="border-rose-600/50 text-rose-300 hover:bg-rose-500/10 h-9 text-[11px]"
                  data-testid="sched-clear-week"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Vider
                </Button>
              </>
            )}
          </>
        )}
        <Button
          size="sm" variant="outline"
          onClick={printSchedule} disabled={loading}
          className="border-slate-600 text-slate-200 h-9 text-[11px]"
          data-testid="sched-print"
        >
          <Printer className="w-3.5 h-3.5 mr-1" /> PDF
        </Button>
        <Button onClick={load} disabled={loading} size="sm" variant="outline" className="border-slate-600 text-slate-200 h-9">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {/* Légende horaires */}
      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        {TEAMS.map((t) => {
          const Icon = t.icon;
          return (
            <span key={t.key} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800/60 border border-slate-700">
              <Icon className="w-3.5 h-3.5" style={{ color: t.accent }} />
              <b className="text-white">{t.label}</b>
              <span className="text-slate-400">{t.hours}</span>
            </span>
          );
        })}
        <span className="text-slate-500 text-[10.5px] ml-auto">
          {data?.count || 0} assignation{(data?.count || 0) > 1 ? "s" : ""} · {data?.employees?.length || 0} employé{(data?.employees?.length || 0) > 1 ? "s" : ""} actif{(data?.employees?.length || 0) > 1 ? "s" : ""}
        </span>
      </div>

      {/* Bandeau workflow validation */}
      {pendingCount > 0 && (
        <div
          className={`rounded-lg px-3 py-2 flex items-center gap-2 border ${
            isAdmin
              ? "bg-amber-500/10 border-amber-500/40 text-amber-100"
              : "bg-blue-500/10 border-blue-500/40 text-blue-100"
          }`}
          data-testid="sched-pending-banner"
        >
          <span className="text-lg">⏳</span>
          <div className="text-[11.5px] leading-tight flex-1">
            {isAdmin ? (
              <>
                <b>{pendingCount} entrée{pendingCount > 1 ? "s" : ""} en attente</b> de validation par vous. Cliquez sur ✓ à côté d&apos;un badge, ou utilisez « Valider la semaine » pour tout valider.
              </>
            ) : (
              <>
                <b>{pendingCount} entrée{pendingCount > 1 ? "s" : ""} en attente</b> de validation par l&apos;Administrateur. Vos ajouts sont visibles mais restent modifiables tant qu&apos;ils ne sont pas validés.
              </>
            )}
          </div>
        </div>
      )}

      {/* Grille planning */}
      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Chargement…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-700 bg-slate-800/40">
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="bg-slate-900/70 text-slate-300 text-[10px] uppercase tracking-wider">
                <th className="text-left px-2 py-2 font-bold w-[140px] sticky left-0 bg-slate-900/90 z-10">Section</th>
                {DAYS.map((d, i) => {
                  const dayD = addDays(weekMonday, i);
                  const isToday = toISO(dayD) === toISO(new Date());
                  return (
                    <th key={d.key} className={`text-center px-1.5 py-2 font-bold border-l border-slate-700 ${isToday ? "bg-amber-500/10 text-amber-300" : ""}`}>
                      {d.short}
                      <div className="text-slate-500 text-[9.5px] font-normal mt-0.5">{fmtDayShort(dayD)}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {SECTIONS.map((s) => {
                const SIcon = s.icon;
                return (
                  <tr key={s.key} className="border-t border-slate-700/50">
                    <td className="px-2 py-1.5 font-bold sticky left-0 bg-slate-800/95 z-10 border-r border-slate-700">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ background: `${s.accent}22`, border: `1px solid ${s.accent}55` }}>
                          <SIcon className="w-4 h-4" style={{ color: s.accent }} />
                        </div>
                        <span className="text-white text-[12px] leading-tight">{s.label}</span>
                      </div>
                    </td>
                    {DAYS.map((_, dayIdx) => (
                      <td key={dayIdx} className="border-l border-slate-700/50 align-top p-0" style={{ minWidth: 128 }}>
                        {TEAMS.map((t) => {
                          const entries = cellIndex.get(`${dayIdx}|${t.key}|${s.key}`) || [];
                          return (
                            <div key={t.key} className="border-b border-slate-700/40 last:border-b-0 p-1" style={{ background: entries.length ? `${t.accent}0a` : "transparent" }}>
                              <div className="flex items-center gap-1 mb-0.5">
                                <t.icon className="w-2.5 h-2.5" style={{ color: t.accent }} />
                                <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: t.accent }}>{t.label}</span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {entries.map((e) => {
                                  const isPending = e.status === "pending";
                                  return (
                                  <span
                                    key={e.id}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-white text-[10px] font-semibold group ${
                                      isPending
                                        ? "bg-amber-500/25 ring-1 ring-amber-400/50 ring-dashed"
                                        : "bg-slate-700/70"
                                    }`}
                                    title={
                                      isPending
                                        ? `${e.salary_name} · Proposé par ${e.created_by || "Resp. Op."} · En attente Admin`
                                        : `${e.salary_name}${e.role_title ? " · " + e.role_title : ""}${e.validated_by ? " · Validé par " + e.validated_by : ""}`
                                    }
                                    data-testid={`sched-entry-${e.id}`}
                                  >
                                    {isPending && <span className="text-amber-300 text-[9px]" title="En attente de validation">⏳</span>}
                                    {e.salary_name.split(" ").slice(0, 2).join(" ")}
                                    {canEdit && (
                                      <>
                                        {isPending && isAdmin && (
                                          <button
                                            type="button"
                                            onClick={() => validateEntry(e.id)}
                                            className="text-emerald-300 hover:text-emerald-200 opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Valider"
                                            data-testid={`sched-validate-${e.id}`}
                                          >
                                            <Check className="w-2.5 h-2.5" strokeWidth={3} />
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => removeEntry(e.id)}
                                          className="text-slate-400 hover:text-rose-300 opacity-0 group-hover:opacity-100 transition-opacity"
                                          title={isAdmin || isPending ? "Retirer" : "Retrait réservé à l'Admin"}
                                        >
                                          <X className="w-2.5 h-2.5" />
                                        </button>
                                      </>
                                    )}
                                  </span>
                                );})}
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() => setPickerCell({ day: dayIdx, team: t.key, section: s.key })}
                                    className="inline-flex items-center justify-center w-6 h-5 rounded border border-dashed border-slate-600 text-slate-500 hover:text-white hover:border-slate-400 hover:bg-slate-700/50 transition"
                                    title="Assigner un employé"
                                    data-testid={`sched-add-${s.key}-${dayIdx}-${t.key}`}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Picker employé */}
      <Dialog open={!!pickerCell} onOpenChange={(v) => { if (!v) { setPickerCell(null); setPickerSearch(""); } }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md" data-testid="sched-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-300" />
              Assigner un employé
            </DialogTitle>
            {pickerCell && (
              <DialogDescription className="text-slate-400 text-xs">
                <b className="text-slate-300">{SECTIONS.find(s => s.key === pickerCell.section)?.label}</b>
                {" · "}
                <b className="text-slate-300">{DAYS[pickerCell.day]?.label}</b>
                {" · "}
                <b className="text-slate-300">{TEAMS.find(t => t.key === pickerCell.team)?.label} ({TEAMS.find(t => t.key === pickerCell.team)?.hours})</b>
              </DialogDescription>
            )}
          </DialogHeader>
          <Input
            placeholder="Rechercher un employé (nom, poste, matricule)…"
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
            className="bg-slate-700/60 border-slate-600 text-white h-9"
            data-testid="sched-picker-search"
            autoFocus
          />
          <div className="max-h-[320px] overflow-y-auto space-y-1 pr-1">
            {filteredEmployees.length === 0 ? (
              <p className="text-slate-500 text-[11.5px] text-center py-6 italic">Aucun employé trouvé</p>
            ) : filteredEmployees.map((emp) => {
              const already = currentCellSalaryIds.has(emp.id);
              return (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => !already && assign(emp.id)}
                  disabled={already || busy}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                    already
                      ? "bg-slate-700/30 border-slate-700 opacity-50 cursor-not-allowed"
                      : "bg-slate-900/50 border-slate-700 hover:border-emerald-500/50 hover:bg-slate-700/40 cursor-pointer"
                  }`}
                  data-testid={`sched-picker-emp-${emp.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-[12.5px]">{emp.name}</div>
                      <div className="text-slate-500 text-[10.5px]">
                        {emp.role_title || "—"}
                        {emp.matricule && <span className="text-slate-700"> · {emp.matricule}</span>}
                      </div>
                    </div>
                    {already && (
                      <span className="text-[10px] text-amber-400 font-bold uppercase tracking-wider">Déjà assigné</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WeeklyScheduleTab;
